import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { FormComponent, INPUT_MASKS, INPUT_PLACEHOLDERS, SharedFormOptions } from '@project/shared';
import { ISelectOption } from '@lib/ui/form-elements';
import { FormBuilder, ValidatorFn } from '@angular/forms';
import { IPatient, IPatientToInvite, IUserToInvite } from '@project/view-models';
import { PatientInviteUtils } from '../patient-invite.common';
import { EPatientsModuleAction, PatientInviteManagerService } from '../patient-invite-manager.service';
import { fillWithZeros } from 'src/app/core/helpers/utils/numbers/numbers';
import { DateUtils, LibValidators, TGuid } from '@core/helpers';
import { v4 as uuid } from 'uuid';
import { TranslateService } from '@project/translate';
import { NotificationsService } from '@lib/notifications';
import { HcFeature, HcRule, HcService } from '@project/services';
import { HCConfigFeatures, HCConfigRules } from 'src/hc-config/hc-config';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-patient-invite-details-form',
  templateUrl: './patient-invite-details-form.component.html',
  styleUrls: ['./patient-invite-details-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PatientInviteDetailsFormComponent extends FormComponent implements OnInit, OnDestroy {
  @Input() patient: IPatient;
  @Input() isPending = false;
  @Input() fatherPatientId?: TGuid = null;
  @Input() fatherTagId?: TGuid = null;

  @Output() dataSubmit = new EventEmitter<IPatientToInvite>();
  @Output() dataSignup = new EventEmitter<IUserToInvite>();
  @Output() dataDependent = new EventEmitter<IPatientToInvite>();

  public form = this.fb.group(PatientInviteUtils.getPatientFormDeclaration());

  public readonly genderOptions: ISelectOption[] = SharedFormOptions.genderOptions();
  public readonly dateInputMask = INPUT_MASKS.date;
  public readonly dateInputPlaceholder = INPUT_PLACEHOLDERS.date;
  public readonly tagFilter$ = this.patientInviteManagerService.tagFilter$;

  public readonly phoneInputMask = INPUT_MASKS.phone;
  public readonly dobInputMask = INPUT_MASKS.dateOfBirth;
  public readonly cpfInputMask = INPUT_MASKS.cpf;

  private destroy$ = new Subject<void>();
  // Cancels subscriptions bound to the current `this.form` instance (guardian reactivity)
  // whenever the form is rebuilt, so a remote HC config change doesn't leak a subscription to
  // the discarded form on every toggle.
  private formDestroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private patientInviteManagerService: PatientInviteManagerService,
    private notificationsService: NotificationsService,
    public hcService: HcService,
    private cdr: ChangeDetectorRef,
  ) {
    super();
  }

  @HcFeature(HCConfigFeatures.PATIENT_INVITE_AND_EDIT_FORM_INCLUDE_CNS)
  public readonly hcHasCns: boolean;

  @HcFeature(HCConfigFeatures.PATIENT_INVITE_AND_EDIT_FORM_INCLUDE_EXTERNAL_ID)
  public readonly hcHasExternalId: boolean;

  @HcFeature(HCConfigFeatures.PATIENT_COMPLEMENTARY_DATA_FORM_INCLUDE_ATYPICAL_CHILDREN_GUARDIAN)
  public readonly hcIncludeAtypicalChildrenGuardian: boolean;

  @HcRule(HCConfigRules.PATIENT_INVITE_AND_EDIT_FORM_REQUIRE_EMAIL)
  public readonly hcRequireEmail: boolean;

  @HcRule(HCConfigRules.PATIENT_INVITE_AND_EDIT_FORM_REQUIRE_CNS)
  public readonly hcRequireCns: boolean;

  @HcRule(HCConfigRules.PATIENT_INVITE_AND_EDIT_FORM_REQUIRE_CPF)
  public readonly hcRequireCpf: boolean;

  @HcRule(HCConfigRules.PATIENT_INVITE_AND_EDIT_FORM_REQUIRE_CPF_OR_EMAIL)
  public readonly hcRequireCpfOrEmail: boolean;

  @HcRule(HCConfigRules.PATIENT_INVITE_AND_EDIT_FORM_REQUIRE_CPF_OR_EMAIL_OR_CNS)
  public readonly hcRequireCpfOrEmailOrCns: boolean;

  @HcRule(HCConfigRules.PATIENT_INVITE_AND_EDIT_FORM_REQUIRE_EXTERNAL_ID)
  public readonly hcRequireExternalId: boolean;

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.formDestroy$.next();
    this.formDestroy$.complete();
  }

  protected onFormInit(): void {
    this.buildForm();
    this.attemptToPopulateForm();
    this.wireFormInstanceSubscriptions();

    // Remote HC config can change in realtime (which fields are shown/required). Rebuilds the
    // form live - including while the user has already filled some of it in - preserving
    // whatever values still map to a control after the rebuild.
    this.hcService.configUpdated$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      const previousValue = this.form.getRawValue();
      this.buildForm();
      this.form.patchValue(previousValue);
      this.wireFormInstanceSubscriptions();
      this.cdr.detectChanges();
    });
  }

  private buildForm(): void {
    const hasNoFatherDependency = !this.fatherPatientId;
    if (hasNoFatherDependency) this.initializeNewPatientForm();
    else this.initializeNewDependentPatientForm();

    if (!this.form.contains('isAtypicalChildGuardian')) {
      this.form.addControl('isAtypicalChildGuardian', this.fb.control(null));
    }
  }

  // (Re)wires subscriptions bound to the current `this.form` instance. Safe to call again after
  // the form is rebuilt: formDestroy$ tears down whatever was bound to the previous instance.
  private wireFormInstanceSubscriptions(): void {
    this.formDestroy$.next();
    this.setupAtypicalGuardianReactivity();
  }

  private initializeNewPatientForm(): void {
    const skippedFields = this.getSkippedFieldsByHc();
    const requiredFields = this.getRequiredFieldsByHc();
    const formDeclaration = PatientInviteUtils.getPatientFormDeclaration(skippedFields, requiredFields);
    this.form = this.fb.group(formDeclaration, { validators: this.getCustomValidations() });
  }

  private initializeNewDependentPatientForm(): void {
    const skippedFields = this.getSkippedFieldsByHc();
    const requiredFields = this.getRequiredFieldsByHc();
    const formDeclaration = PatientInviteUtils.getPatientInviteDependentFormDeclaration(skippedFields, requiredFields);
    this.form = this.fb.group(formDeclaration, { validators: this.getCustomValidations() });
    this.patientInviteManagerService.dispatchAction(EPatientsModuleAction.SetTagById, this.fatherTagId);
    this.form.get('tag').setValue(this.fatherTagId);
  }

  private getCustomValidations(): ValidatorFn[] {
    const validations = [];

    if (this.hcRequireCpfOrEmail) {
      const cpfOrEmailValidation = LibValidators.cpfRequiredIfEmailEmpty(
        TranslateService.localize('validations.cpf-required-if-email-empty'),
      );
      validations.push(cpfOrEmailValidation);
    }

    if (this.hcRequireCpfOrEmailOrCns) {
      const cpfOrEmailOrCnsValidation = LibValidators.validateRequiredCpfOrEmailOrCns(
        'validations.cpf-or-email-or-cns-required',
      );
      validations.push(cpfOrEmailOrCnsValidation);
    }

    return validations;
  }

  private setDefaultGender(): void {
    this.form.get('gender').setValue(this.genderOptions[0].value);
  }

  private attemptToPopulateForm(): void {
    if (!this.patient) return;

    this.form.patchValue({
      firstName: this.patient.firstName,
      lastName: this.patient.lastName,
      email: this.patient.email,
      gender: this.patient.gender,
      phone: this.patient.phone,
      cpf: this.patient.cpfNumber,
      externalId: this.patient.external_id,
      cns: this.patient.cns,
      isAtypicalChildGuardian: this.patient.isAtypicalChildGuardian ?? null,
    });

    if (this.patient.birthDate) {
      const day = fillWithZeros(this.patient.birthDate.getUTCDate(), 2);
      const month = fillWithZeros(this.patient.birthDate.getUTCMonth() + 1, 2);
      const year = this.patient.birthDate.getUTCFullYear();
      this.form.get('dateOfBirth').setValue(`${day}.${month}.${year}`);
    }

    if (this.patient.tags && this.patient.tags.length > 0) {
      this.patientInviteManagerService.dispatchAction(EPatientsModuleAction.SetTagById, this.patient.tags[0].id);
    }
  }

  private setupAtypicalGuardianReactivity(): void {
    if (!this.hcIncludeAtypicalChildrenGuardian) {
      return;
    }

    const dateOfBirthControl = this.form.get('dateOfBirth');

    if (!dateOfBirthControl) {
      return;
    }

    this.evaluateGuardianControl(dateOfBirthControl.value);

    dateOfBirthControl.valueChanges
      .pipe(takeUntil(this.formDestroy$), takeUntil(this.destroy$))
      .subscribe((dateValue: string) => {
        this.evaluateGuardianControl(dateValue);
      });
  }

  private evaluateGuardianControl(dateOfBirthValue: string): void {
    const guardianControl = this.form.get('isAtypicalChildGuardian');

    if (!guardianControl) {
      return;
    }

    const age = DateUtils.calculateAge(dateOfBirthValue);
    const isMinorOrInvalidAge = age === null || age < 18;

    if (isMinorOrInvalidAge) {
      guardianControl.setValue(null, { emitEvent: false });
      guardianControl.disable({ emitEvent: false });
    } else {
      guardianControl.enable({ emitEvent: false });
    }
  }

  protected doCleanSubmit() {
    this.patientInviteManagerService.dispatchAction(EPatientsModuleAction.SetTagSelected);
    this.form.get('tag').patchValue(this.tagFilter$.value);

    if (!this.form.get('tag').value) {
      this.notificationsService.error({
        message: TranslateService.localize('errors.required-group'),
      });
      return;
    }

    if (this.form.hasError('cpfRequiredIfEmailEmpty')) {
      this.notificationsService.error({
        message: TranslateService.localize('validations.cpf-required-if-email-empty'),
      });
      return;
    }

    const isDependent = this.fatherPatientId;

    if (isDependent) {
      this.form.get('email').patchValue(uuid() + '+dep@gmail.com');
      this.form.controls['email'].disable();

      const userData = PatientInviteUtils.createPatientInviteDto(this.form.getRawValue());
      this.dataDependent.emit(userData);

      this.form.reset();
      this.setDefaultGender();
      this.form.controls['email'].disable();

      return;
    }

    const emailValue = this.form.get('email').value;
    const userData = PatientInviteUtils.createPatientInviteDto(this.form.value);

    if (emailValue && emailValue.trim().length > 0) {
      this.dataSubmit.emit(userData);
    } else {
      this.dataSignup.emit(userData);
    }

    if (this.patient != null) {
      return;
    }

    const { gender } = this.form.value;
    this.form.reset();
    this.form.get('gender').patchValue(gender);
    this.patientInviteManagerService.dispatchAction(EPatientsModuleAction.ClearTagSelected);
    if (this.fatherPatientId) {
      this.form.controls['email'].disable();
      this.form.get('tag').setValue(this.fatherTagId);
      this.patientInviteManagerService.dispatchAction(EPatientsModuleAction.SetTagById, this.fatherTagId);
    }
  }

  private getSkippedFieldsByHc(): string[] {
    const skippedFields = [];

    if (!this.hcHasCns) {
      skippedFields.push('cns');
    }

    if (!this.hcHasExternalId) {
      skippedFields.push('externalId');
    }

    return skippedFields;
  }

  private getRequiredFieldsByHc(): string[] {
    const requiredFields = [];

    if (this.hcRequireCpf) requiredFields.push('cpf');
    if (this.hcRequireExternalId) requiredFields.push('externalId');
    if (this.hcRequireCns) requiredFields.push('cns');
    if (this.hcRequireEmail) requiredFields.push('email');

    return requiredFields;
  }
}
